import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2, LogOut, RefreshCw } from 'lucide-react';
import { Background } from '../components/game/Background';
import { Mascot } from '../components/game/Mascot';
import { api } from '../lib/api';

const PERKS = [
  'Todas as tabuadas de 1 a 10',
  'Aprender cantando com 6 vozes',
  'Jogos de perguntas e revisão',
  'Progresso salvo na sua conta',
  'Acesso para sempre — pagamento único',
];

export default function PaywallScreen({ user, confirming, notice, onRecheck, onLogout }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pay = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await api.checkout();
      if (data.already_paid) {
        onRecheck();
        return;
      }
      window.location.assign(data.checkout_url);
    } catch (e) {
      setError(e.detail || 'Não foi possível iniciar o pagamento. Tente novamente.');
      setBusy(false);
    }
  };

  return (
    <Background>
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center px-5 py-8">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <Mascot mood="cheer" size={130} />
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="card-soft mt-2 w-full p-6"
        >
          <p className="text-center font-fred text-sm font-semibold text-purple-600">
            Olá, {user?.name?.split(' ')[0] || 'amigo'}! 👋
          </p>
          <h1 className="mb-1 text-center font-fred text-3xl font-bold text-indigo-900">
            Acesso Vitalício
          </h1>
          <p className="mb-4 text-center text-4xl font-black text-emerald-500">
            R$ 14,90
            <span className="ml-1 align-middle text-sm font-bold text-slate-400">pagamento único</span>
          </p>

          <ul className="mb-5 space-y-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm font-bold text-slate-700">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400 text-white">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {p}
              </li>
            ))}
          </ul>

          {notice === 'pending' && !confirming && (
            <p
              className="mb-3 rounded-2xl bg-amber-100 px-4 py-3 text-center text-sm font-bold text-amber-700"
              data-testid="pay-notice-pending"
            >
              ⏳ Seu pagamento está sendo processado. Assim que for aprovado, o acesso será
              liberado automaticamente. Toque em "Já paguei" para atualizar.
            </p>
          )}
          {notice === 'failure' && !confirming && (
            <p
              className="mb-3 rounded-2xl bg-rose-100 px-4 py-3 text-center text-sm font-bold text-rose-600"
              data-testid="pay-notice-failure"
            >
              O pagamento não foi concluído. Você pode tentar novamente quando quiser. 💛
            </p>
          )}

          {confirming ? (
            <div className="mb-2 flex items-center justify-center gap-2 rounded-2xl bg-amber-100 px-4 py-3 font-black text-amber-700">
              <Loader2 className="h-5 w-5 animate-spin" /> Confirmando seu pagamento...
            </div>
          ) : (
            <button
              data-testid="pay-btn"
              onClick={pay}
              disabled={busy}
              className="btn-3d w-full bg-sky-500 px-6 py-4 text-lg text-white"
              style={{ borderColor: '#0284C7' }}
            >
              {busy ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin" /> Abrindo pagamento...
                </>
              ) : (
                'Pagar com Mercado Pago'
              )}
            </button>
          )}

          {error && (
            <p className="mt-3 text-center text-sm font-bold text-rose-500" data-testid="pay-error">
              {error}
            </p>
          )}

          <div className="mt-4 flex items-center justify-between text-xs font-bold text-slate-400">
            <button data-testid="recheck-btn" onClick={onRecheck} disabled={confirming} className="flex items-center gap-1 hover:text-purple-500 disabled:opacity-50">
              <RefreshCw className="h-4 w-4" /> Já paguei
            </button>
            <button data-testid="logout-btn" onClick={onLogout} className="flex items-center gap-1 hover:text-rose-500">
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </motion.div>
      </div>
    </Background>
  );
}
